"use client"

import { useEffect, useState } from "react"

import { cn } from "@/common/lib/utils"
import { SlidersHorizontal } from "lucide-react"

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/common/components/ui/tabs"
import { UNDERLINE_TABS_LIST, UNDERLINE_TABS_TRIGGER } from "@/common/components/ui/tab-styles"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import FrameworkGallery from "./framework-gallery"
import HeroImpact from "./hero-impact"
import ImpactCharts from "./impact-charts"
import ImpactPanel from "./impact-panel"
import ParameterClusters from "./parameter-clusters"
import { ViewHeader } from "@/modules/lernpfad/components/ui-bits"

/**
 * The HR control room — the fifth view.
 *
 * Two tabs and one panel, and the panel is the point: choosing a framework or
 * moving a slider is only interesting if you can see what it did. It sits
 * outside the tabs so it stays visible from both, because the question
 * "and what does THAT do?" is the same question on either.
 */
export default function SteuerungView({ state }: { state: LernpfadDemoState }) {
    const { t, setOpenCluster } = state
    const [tab, setTab] = useState("frameworks")
    const [jumpTarget, setJumpTarget] = useState<string | null>(null)

    // A framework's cluster pill switches tabs, then asks the parameter column
    // to scroll to that cluster once it has mounted. Two steps, because the
    // section does not exist in the DOM until the tab is on screen.
    useEffect(() => {
        setOpenCluster((cluster: string) => {
            setTab("params")
            setJumpTarget(cluster)
        })
    }, [setOpenCluster])

    return (
        <div className="space-y-5">
            <ViewHeader
                icon={SlidersHorizontal}
                title={t("steuer.title")}
                subtitle={t("steuer.subtitle")}
            />

            <HeroImpact state={state} />

            {/* The rail carries a ONE-PERSON plan diff. Beside the frameworks
                it is a third comparison competing with the population band and
                the two charts, and it is what left ~320 px of empty column
                there. It belongs with the sliders, whose effect on a single
                plan is exactly the question it answers. */}
            <div className={cn("grid gap-6", tab === "params" && "xl:grid-cols-[1fr_320px]")}>
                <div className="min-w-0">
                    <Tabs value={tab} onValueChange={setTab}>
                        <TabsList className={UNDERLINE_TABS_LIST}>
                            <TabsTrigger value="frameworks" className={UNDERLINE_TABS_TRIGGER}>
                                {t("steuer.tab.frameworks")}
                            </TabsTrigger>
                            <TabsTrigger value="params" className={UNDERLINE_TABS_TRIGGER}>
                                {t("steuer.tab.params")}
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="frameworks" className="pt-5">
                            <FrameworkGallery state={state} />
                        </TabsContent>
                        <TabsContent value="params" className="pt-5">
                            <ParameterClusters
                                state={state}
                                jumpTarget={jumpTarget}
                                onJumped={() => setJumpTarget(null)}
                            />
                        </TabsContent>
                    </Tabs>
                </div>

                {tab === "params" && (
                    <aside className="xl:sticky xl:top-[calc(var(--lp-bar-height,72px)+1.5rem)] xl:self-start">
                        <ImpactPanel state={state} />
                    </aside>
                )}
            </div>

            {/* Below the controls, not above them. The band is the payoff you
                watch while turning a knob and has to stay in view; the two
                distribution charts are a read, not a control, and pushing the
                tabs a full viewport down made the point of the screen the last
                thing you found. */}
            <ImpactCharts state={state} />
        </div>
    )
}
